import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import toast from 'react-hot-toast';
import Button from 'components/ui/Button';
import { uploadManualDocumentDocx } from 'services/manualDocumentService';
import { applyManualDocumentUploadToEditor } from 'utils/manualDocumentUpload';

const DOCX_ACCEPT =
  '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * DOCX upload control shared with Elaborar Documento — same API and editor apply flow.
 * Imperative API: `ref.current.open()` opens the file picker.
 */
const ManualDocumentDocxUpload = forwardRef(function ManualDocumentDocxUpload(
  {
    editor,
    disabled = false,
    label = 'DOCX',
    uploading = false,
    onUploadingChange,
    confirmReplace = true,
    allowFieldTokenDeletionOnReplace = false,
    replaceAllEditorContent = null,
    onBeforeReplace = null,
    onSuccess,
    className,
    variant = 'outline',
    iconName = 'FileUp',
    size = 'default',
    hideButton = false,
  },
  ref
) {
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    open: () => {
      if (disabled || uploading) return;
      inputRef.current?.click();
    },
  }));

  const handlePick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editor) return;

    onUploadingChange?.(true);
    try {
      const result = await uploadManualDocumentDocx(file);
      if (!result.ok) {
        toast.error(result.message || 'Não foi possível processar o DOCX.');
        return;
      }

      const applied = applyManualDocumentUploadToEditor(editor, result, 'DOCX', {
        confirmReplace,
        allowFieldTokenDeletionOnReplace,
        replaceAllEditorContent,
        onBeforeReplace,
      });
      if (!applied.ok) {
        toast.error(applied.message);
        return;
      }

      toast.success('Conteúdo do DOCX inserido no editor.');
      onSuccess?.({ editor, replaced: applied.replaced });
    } catch (error) {
      console.error('ManualDocumentDocxUpload', error);
      toast.error('Erro ao importar documento.');
    } finally {
      onUploadingChange?.(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={DOCX_ACCEPT}
        className="sr-only"
        onChange={handlePick}
        aria-label={label}
      />
      {hideButton ? null : (
        <Button
          type="button"
          variant={variant}
          size={size}
          iconName={iconName}
          className={className}
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
        >
          {uploading ? 'Importando…' : label}
        </Button>
      )}
    </>
  );
});

export default ManualDocumentDocxUpload;
